import { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import WarningIcon from '@material-ui/icons/Warning';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { createFormField } from '@backstage/plugin-scaffolder-react/alpha';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export interface UserWithGithub {
  entityRef: string;
  name: string;
  displayName: string;
  email?: string;
  hasMapping: boolean;
  githubUsername?: string;
}

export interface EmployeePickerProps {
  onChange: (value: string[]) => void;
  formData?: string[];
  required?: boolean;
  rawErrors?: string[];
  idSchema?: {
    $id: string;
  };
  schema?: {
    title?: string;
    description?: string;
  };
}

export const EmployeePickerComponent = (props: EmployeePickerProps) => {
  const { onChange, formData = [], rawErrors, idSchema, schema } = props;
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [users, setUsers] = useState<UserWithGithub[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    async function loadUsers() {
      try {
        setLoading(true);
        setFetchError(null);
        const baseUrl = await discoveryApi.getBaseUrl('template-provisioning');
        const res = await fetchApi.fetch(`${baseUrl}/users-with-github`);
        if (!res.ok) {
          throw new Error(`Failed to load users: HTTP ${res.status}`);
        }
        const data = await res.json();
        if (isMounted) {
          setUsers(data.items || []);
        }
      } catch (err: any) {
        if (isMounted) {
          setFetchError(err.message || 'Error loading employee list');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadUsers();
    return () => {
      isMounted = false;
    };
  }, [discoveryApi, fetchApi]);

  // Selected UserWithGithub objects
  const selectedUsers = users.filter(u => (formData || []).includes(u.entityRef));

  // Check for any selected users without GitHub mapping
  const unmappedSelected = selectedUsers.filter(u => !u.hasMapping);

  const handleChange = (_event: any, newValues: UserWithGithub[]) => {
    onChange(newValues.map(u => u.entityRef));
  };

  const hasErrors = rawErrors && rawErrors.length > 0;

  return (
    <Box marginY={2}>
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
        {schema?.title ?? 'Assign Collaborators'}
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
        {schema?.description ??
          'Select employees to add as collaborators to the new GitHub repository. Employees without a GitHub mapping cannot be invited.'}
      </Typography>

      {fetchError && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {fetchError}
        </Alert>
      )}

      {unmappedSelected.length > 0 && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          The following selected employee(s) do not have a GitHub username mapping configured in Backstage:{' '}
          <strong>
            {unmappedSelected
              .map(u => u.displayName || u.email || u.name)
              .join(', ')}
          </strong>
          . Creation is blocked until unmapped users are deselected.
        </Alert>
      )}

      <Autocomplete
        multiple
        id={idSchema?.$id ?? 'employee-picker'}
        options={users}
        disableCloseOnSelect
        loading={loading}
        value={selectedUsers}
        // Match the API's stable identity, even when users share a display name.
        getOptionSelected={(option, value) => option.entityRef === value.entityRef}
        getOptionLabel={option =>
          option.displayName
            ? `${option.displayName} (${option.email || option.name})`
            : option.email || option.name
        }
        onChange={handleChange}
        renderOption={(option, { selected }) => (
          <Box display="flex" alignItems="center" width="100%">
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            <Box flexGrow={1}>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {option.displayName || option.name}
              </Typography>
              {option.email && (
                <Typography variant="caption" color="textSecondary">
                  {option.email}
                </Typography>
              )}
            </Box>
            {!option.hasMapping && (
              <Chip
                size="small"
                icon={<WarningIcon fontSize="small" />}
                label="No GitHub mapping"
                color="secondary"
                variant="outlined"
                style={{ marginLeft: 8 }}
              />
            )}
          </Box>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.entityRef}
              label={option.displayName || option.email || option.name}
              color={option.hasMapping ? 'primary' : 'secondary'}
              icon={!option.hasMapping ? <WarningIcon /> : undefined}
            />
          ))
        }
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={selectedUsers.length === 0 ? 'Search employees by name or email...' : ''}
            error={Boolean(hasErrors || unmappedSelected.length > 0)}
            helperText={
              unmappedSelected.length > 0
                ? 'Cannot proceed: unmapped users selected'
                : hasErrors
                ? rawErrors[0]
                : undefined
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export const EmployeePicker = createFormField({
  name: 'EmployeePicker',
  component: EmployeePickerComponent as any,
});
